import {
	ChangeDetectionStrategy,
	Component,
	computed,
	input,
	model,
	signal,
} from '@angular/core';

import { TranslatePipe } from '@ngx-translate/core';
import { SvgIconComponent } from 'angular-svg-icon';

import { CheckboxComponent } from '@valk-nx/components/ui-checkbox/src/lib/checkbox';
import { TagComponent } from '@valk-nx/components/ui-tag/src/lib/tag';

import { FiltersInterface } from './contracts/filters.interface';

interface FilterChipsInterface {
	group: string;
	name: string;
	value: string;
}

@Component({
	changeDetection: ChangeDetectionStrategy.OnPush,
	selector: `vp-filters`,
	templateUrl: './filters.component.html',
	imports: [CheckboxComponent, TagComponent, TranslatePipe, SvgIconComponent],
})
export class FiltersComponent {
	protected readonly ITEMS_LIMIT = 8;

	filters = input<FiltersInterface[]>([]);

	activeFilters = model<Record<string, string[]>>({});

	protected expandedGroups = signal<Set<string>>(new Set());

	activeFilterChips = computed<FilterChipsInterface[]>(() => {
		const active = this.activeFilters();
		const groups = this.filters();

		return Object.entries(active).flatMap(([groupTitle, values]) => {
			const items =
				groups.find((g) => g.title === groupTitle)?.items ?? [];

			return values.map((value) => ({
				group: groupTitle,
				name: items.find((i) => i.value === value)?.name ?? value,
				value,
			}));
		});
	});

	toggleShowMore(groupTitle: string): void {
		this.expandedGroups.update((groups) => {
			const next = new Set(groups);
			next.add(groupTitle);
			return next;
		});
	}

	clearAllFilters(): void {
		this.activeFilters.set({});
	}

	removeFilter(groupTitle: string, value: string): void {
		this.activeFilters.update((current) => {
			const nextGroupValues = (current[groupTitle] ?? []).filter(
				(v) => v !== value,
			);

			if (nextGroupValues.length === 0) {
				const { [groupTitle]: _removed, ...rest } = current;
				return rest;
			}

			return {
				...current,
				[groupTitle]: nextGroupValues,
			};
		});
	}

	onFilterChange(groupTitle: string, itemValue: string, keep: boolean): void {
		this.activeFilters.update((current) => {
			const groupValues = current[groupTitle] ?? [];
			const updated = keep
				? [...new Set([...groupValues, itemValue])]
				: groupValues.filter((v) => v !== itemValue);

			if (updated.length === 0) {
				const { [groupTitle]: _removed, ...withoutGroup } = current;
				return withoutGroup;
			}
			return { ...current, [groupTitle]: updated };
		});
	}
}
